import React, { useState } from "react";

const PatientTable = ({ patients, loading, onSelectPatient, selectedPatientId, showDataCounts = false }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [sortField, setSortField] = useState("created_at");
    const [sortDirection, setSortDirection] = useState("desc");

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(prev => prev === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection("asc");
        }
    };

    const filteredPatients = patients
        .filter(patient => {
            const search = searchTerm.toLowerCase();
            return (
                patient.patient_id?.toLowerCase().includes(search) ||
                patient.name?.toLowerCase().includes(search) ||
                patient.dataset_name?.toLowerCase().includes(search) ||
                patient.notes?.toLowerCase().includes(search)
            );
        })
        .sort((a, b) => {
            let aVal = a[sortField];
            let bVal = b[sortField];

            if (sortField === "created_at") {
                aVal = new Date(aVal);
                bVal = new Date(bVal);
            }

            if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
            if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
            return 0;
        });

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        return new Date(dateString).toLocaleDateString();
    };

    const getGenderLabel = (gender) => {
        const labels = { M: "Male", F: "Female", O: "Other", U: "Unknown" };
        return labels[gender] || "-";
    };

    const SortIcon = ({ field }) => (
        <i className={`fa fa-sort${sortField === field ? (sortDirection === "asc" ? "-up" : "-down") : ""} ms-1`}></i>
    );

    if (loading) {
        return (
            <div className="loading-state">
                <span className="spinner-border spinner-border-sm me-2"></span>
                Loading patients...
            </div>
        );
    }

    if (patients.length === 0) {
        return (
            <div className="empty-state">
                <i className="fa fa-users"></i>
                <p>No patients found</p>
                <small className="text-muted">Create a patient using the form above</small>
            </div>
        );
    }

    return (
        <div className="patient-table-container">
            {/* Search Bar */}
            <div className="table-search mb-3">
                <div className="search-input-wrapper">
                    <i className="fa fa-search search-icon"></i>
                    <input
                        type="text"
                        className="form-control search-input"
                        placeholder="Search patients..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button
                            className="clear-search"
                            onClick={() => setSearchTerm("")}
                        >
                            <i className="fa fa-times"></i>
                        </button>
                    )}
                </div>
                <span className="search-results">
                    Showing {filteredPatients.length} of {patients.length} patients
                </span>
            </div>

            {/* Table */}
            <table className="patients-table">
                <thead>
                    <tr>
                        <th onClick={() => handleSort("patient_id")} className="sortable">
                            Patient ID <SortIcon field="patient_id" />
                        </th>
                        <th onClick={() => handleSort("name")} className="sortable">
                            Name <SortIcon field="name" />
                        </th>
                        <th onClick={() => handleSort("dataset_name")} className="sortable">
                            Dataset <SortIcon field="dataset_name" />
                        </th>
                        <th>Date of Birth</th>
                        <th>Gender</th>
                        {showDataCounts && (
                            <th>Data Items</th>
                        )}
                        <th onClick={() => handleSort("created_at")} className="sortable">
                            Created <SortIcon field="created_at" />
                        </th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredPatients.map((patient) => (
                        <tr
                            key={patient.id}
                            className={selectedPatientId === patient.id ? "selected" : ""}
                        >
                            <td>
                                <strong>{patient.patient_id}</strong>
                            </td>
                            <td>{patient.name || "-"}</td>
                            <td>
                                {patient.dataset_name ? (
                                    <span className="badge bg-info">{patient.dataset_name}</span>
                                ) : (
                                    <span className="text-muted">-</span>
                                )}
                            </td>
                            <td>{formatDate(patient.date_of_birth)}</td>
                            <td>
                                <span className={`badge bg-${patient.gender === 'M' ? 'primary' : patient.gender === 'F' ? 'danger' : 'secondary'}`}>
                                    {getGenderLabel(patient.gender)}
                                </span>
                            </td>
                            {showDataCounts && patient.data_count && (
                                <td>
                                    <div className="data-count-badges">
                                        {patient.data_count.imaging > 0 && (
                                            <span className="badge bg-primary" title="Medical Imaging">
                                                <i className="fa fa-x-ray me-1"></i>{patient.data_count.imaging}
                                            </span>
                                        )}
                                        {patient.data_count.image > 0 && (
                                            <span className="badge bg-success" title="Images">
                                                <i className="fa fa-image me-1"></i>{patient.data_count.image}
                                            </span>
                                        )}
                                        {patient.data_count.document > 0 && (
                                            <span className="badge bg-warning" title="Documents">
                                                <i className="fa fa-file-text me-1"></i>{patient.data_count.document}
                                            </span>
                                        )}
                                        {patient.data_count.table > 0 && (
                                            <span className="badge bg-info" title="Tables">
                                                <i className="fa fa-table me-1"></i>{patient.data_count.table}
                                            </span>
                                        )}
                                        {patient.data_count.signal > 0 && (
                                            <span className="badge bg-secondary" title="Signals">
                                                <i className="fa fa-wave-square me-1"></i>{patient.data_count.signal}
                                            </span>
                                        )}
                                        {patient.data_count.total === 0 && (
                                            <span className="text-muted">No data</span>
                                        )}
                                    </div>
                                </td>
                            )}
                            <td>{formatDate(patient.created_at)}</td>
                            <td>
                                <button
                                    className={`btn btn-sm ${selectedPatientId === patient.id ? 'btn-success' : 'btn-outline-primary'}`}
                                    onClick={() => onSelectPatient(patient)}
                                >
                                    {selectedPatientId === patient.id ? (
                                        <>
                                            <i className="fa fa-check me-1"></i>
                                            Selected
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa fa-hand-pointer me-1"></i>
                                            Select
                                        </>
                                    )}
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default PatientTable;
